import { useEffect, useState, useCallback } from "react";
import "./App.css";
import {
  Provider,
  ActionButton,
  View,
  Heading,
  Image,
  Flex,
  Grid,
  repeat,
  Form,
  TextField,
  lightTheme,
  TooltipTrigger,
  Tooltip,
} from "@adobe/react-spectrum";
import { Connection, PublicKey, clusterApiUrl } from "@solana/web3.js";
import {
  Program,
  Provider as AnchorProvider,
  web3,
} from "@project-serum/anchor";

import idl from "./utils/giftproject.json";
import kp from "./utils/keypair.json";

// SystemProgram is a reference to the Solana runtime!
// const { SystemProgram, Keypair } = web3;
const { SystemProgram } = web3;

// Create a keypair for the account that will hold the GIF data.
// let baseAccount = Keypair.generate();
const arr = Object.values(kp._keypair.secretKey);
const secret = new Uint8Array(arr);
const baseAccount = web3.Keypair.fromSecretKey(secret);

// Get our program's id form the IDL file.
const programID = new PublicKey(idl.metadata.address);

// Set our network to devent.
const network = clusterApiUrl("devnet");

// Control's how we want to acknowledge when a trasnaction is "done".
const opts = {
  preflightCommitment: "processed",
};

// Constants
// const TEST_GIFS = [
//   "https://i.giphy.com/media/eIG0HfouRQJQr1wBzz/giphy.webp",
//   "https://media4.giphy.com/media/l0HlDDyxBfSaPpU88/giphy.gif?cid=ecf05e47r6e2omecdpnwyrjru1m0amakv3dc70tpntbio59h&rid=giphy.gif&ct=g",
//   "https://i.imgur.com/aVvnp6D.gif",
//   "https://media2.giphy.com/media/l4pTky87QVuvbzwnS/giphy.gif?cid=ecf05e471w46qmoli04g5phtgdoo324p1v2choggz0ztu11w&rid=giphy.gif&ct=g",
//   "https://c.tenor.com/hBImNYbwIlgAAAAC/stock-market.gif",
//   "https://c.tenor.com/u4lVOUenF1cAAAAC/i-love-that-stock-interview.gif",
// ];

const App = () => {
  const [walletAddress, walletAddressSet] = useState(null);
  const [linkValue, linkValueSet] = useState("");
  const [gifList, gifListSet] = useState(null);

  const checkIfWalletIsConnected = async () => {
    try {
      const { solana } = window;

      if (solana) {
        if (solana.isPhantom) {
          console.log("phantom wallet found");

          const response = await solana.connect({ onlyIfTrusted: true });
          console.log(
            `connected with public key: ${response.publicKey.toString()}`
          );

          walletAddressSet(response.publicKey.toString());
        }
      } else {
        alert("solana object not found! get a phantom wallet");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const connectWallet = async () => {
    const { solana } = window;

    if (solana) {
      const response = await solana.connect();
      console.log("Connected with Public Key:", response.publicKey.toString());
      walletAddressSet(response.publicKey.toString());
    }
  };

  const renderNotConnectedContainer = () => (
    <ActionButton onPress={connectWallet}>Connect to Wallet</ActionButton>
  );

  const renderConnectedContainer = () => {
    if (!gifList) {
      return (
        <Grid
          columns={repeat("auto-fit", "size-3000")}
          justifyContent="center"
          gap="size-100"
        >
          <ActionButton onPress={createGifAccount}>
            Initialize GIF Program Account
          </ActionButton>
        </Grid>
      );
    } else {
      return (
        <Grid
          columns={repeat("auto-fit", "size-3000")}
          justifyContent="center"
          gap="size-100"
        >
          {gifList.map((val) => (
            <View key={val.gifLink}>
              <Image src={val.gifLink} alt={val.gifLink} />
              <TooltipTrigger delay={0}>
                <ActionButton width="size-3000" isQuiet>
                  address
                </ActionButton>
                <Tooltip>{val.userAddress.toString()}</Tooltip>
              </TooltipTrigger>
            </View>
          ))}
        </Grid>
      );
    }
  };

  const getGifList = useCallback(async () => {
    try {
      const provider = getProvider();
      const program = new Program(idl, programID, provider);
      const account = await program.account.baseAccount.fetch(
        baseAccount.publicKey
      );

      console.log("Got the account", account);
      gifListSet(account.gifList);
    } catch (error) {
      console.log("Error in getGifList: ", error);
      gifListSet(null);
    }
  }, []);

  const sendGif = useCallback(
    async (event) => {
      event.preventDefault();

      if (linkValue.length === 0) {
        console.log("No gif link given!");
        return;
      }

      console.log("Gif link:", linkValue);

      try {
        const provider = getProvider();
        const program = new Program(idl, programID, provider);

        await program.rpc.addGif(linkValue, {
          accounts: {
            baseAccount: baseAccount.publicKey,
          },
        });

        console.log("GIF sucesfully sent to program", linkValue);

        await getGifList();
      } catch (error) {
        console.log("Error sending GIF:", error);
      }
    },
    [getGifList, linkValue]
  );

  const getProvider = () => {
    const connection = new Connection(network, opts.preflightCommitment);
    const provider = new AnchorProvider(
      connection,
      window.solana,
      opts.preflightCommitment
    );

    return provider;
  };

  const createGifAccount = async () => {
    try {
      const provider = getProvider();
      const program = new Program(idl, programID, provider);

      console.log("ping");

      await program.rpc.initialize({
        accounts: {
          baseAccount: baseAccount.publicKey,
          user: provider.wallet.publicKey,
          systemProgram: SystemProgram.programId,
        },
        signers: [baseAccount],
      });

      console.log(
        "Created a new BaseAccount w/ address:",
        baseAccount.publicKey.toString()
      );
      await getGifList();
    } catch (error) {
      console.log("Error creating BaseAccount account:", error);
    }
  };

  useEffect(() => {
    window.addEventListener("load", async (event) => {
      await checkIfWalletIsConnected();
    });

    return () =>
      window.removeEventListener("load", async (event) => {
        await checkIfWalletIsConnected();
      });
  }, []);

  useEffect(() => {
    if (walletAddress) {
      console.log("Fetching GIF list...");

      getGifList();
    }
  }, [walletAddress, getGifList]);

  return (
    <Provider theme={lightTheme}>
      <View paddingBottom="size-200">
        <Flex direction="column" gap="size-100" alignItems="center">
          <View width="size-3000" marginTop="size-500">
            <Image
              alt="portal"
              src="https://media2.giphy.com/media/l0Exm2Wa4TermgtJC/giphy.gif?cid=790b76112e66f5f041c7d3f0b49b5e4c6448bf12cc48a8f8&rid=giphy.gif&ct=g"
            />
          </View>
          <View>
            <Heading level={2}>Do you feel dizzy?</Heading>
          </View>
          <View>{!walletAddress && renderNotConnectedContainer()}</View>
        </Flex>
        {walletAddress && (
          <>
            <Grid
              columns={repeat("auto-fit", "size-3400")}
              justifyContent="center"
              gap="size-100"
            >
              <View>
                <Form
                  width="100%"
                  isRequired
                  necessityIndicator="label"
                  onSubmit={sendGif}
                >
                  <TextField
                    label="GIF Link"
                    type="text"
                    placeholder="Enter gif link!"
                    value={linkValue}
                    onChange={linkValueSet}
                  />
                  <ActionButton type="submit">Submit</ActionButton>
                </Form>
              </View>
            </Grid>
            <View marginTop="size-300">{renderConnectedContainer()}</View>
          </>
        )}
      </View>
    </Provider>
  );
};

export default App;
